import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Card, Button, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Standard Header Card - matches BillingDashboard, CareCoordination, etc.
export const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

/**
 * Page Header Component with back navigation
 * Provides consistent header styling with optional back button
 * Scrolls to top on mount to ensure header is visible
 */
export const PageHeader = ({ 
  title, 
  subtitle, 
  icon,
  backTo,
  onBack,
  actions,
  children 
}) => {
  const navigate = useNavigate();
  
  // Scroll to top when the header mounts to ensure visibility
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        py: 2,
        px: 3,
        mb: 3,
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title="Go back">
          <IconButton onClick={handleBack} sx={{ color: 'white' }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'white', opacity: 0.9 }}>
            {React.cloneElement(icon, { sx: { fontSize: 36 } })}
          </Box>
        )}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      
      {(actions || children) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actions}
          {children}
        </Box>
      )}
    </Box>
  );
};

// Compact Header Button for actions in header
export const HeaderButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  minWidth: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
}));

// Stats Card with hover effect
export const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

// Content Card with consistent styling
export const ContentCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
}));

// Page Container with gradient background
export const PageContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

// Section Header Typography
export const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

// Action Card with hover highlight
export const ActionCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.main,
  },
}));
